import React, { useEffect, useState, useRef } from 'react';
import { audioManager } from '@/utils/audioManager';
import { GAME_SOUNDS } from '@/config/soundEffects';

interface CountdownScreenProps {
  isTeacher?: boolean;
  onCountdownComplete?: () => void;
  initialCount?: number;
  sessionId?: string;
  playerId?: string;
  timeRemaining?: number;
}

const CountdownScreen: React.FC<CountdownScreenProps> = ({
  isTeacher = false,
  onCountdownComplete,
  initialCount = 3,
  timeRemaining,
}) => {
  const [countdown, setCountdown] = useState(initialCount);
  const [isAnimating, setIsAnimating] = useState(false);
  const previousDisplayCountRef = useRef<number | null>(null);
  const isSyncedCountdown = typeof timeRemaining === 'number';
  const displayCount = isSyncedCountdown ? Math.max(0, timeRemaining) : countdown;

  // Use ref to store stable callback reference and prevent effect from restarting
  const onCountdownCompleteRef = useRef(onCountdownComplete);
  useEffect(() => {
    onCountdownCompleteRef.current = onCountdownComplete;
  }, [onCountdownComplete]);

  useEffect(() => {
    if (isSyncedCountdown) {
      return;
    }

    if (countdown > 0) {
      // Stop any previously playing countdown tick to prevent overlap
      if (audioManager.isReady()) {
        audioManager.stop(GAME_SOUNDS.COUNTDOWN_TICK.id);
        // Play countdown tick sound
        audioManager.play(GAME_SOUNDS.COUNTDOWN_TICK.id);
      }

      // Trigger animation
      setIsAnimating(true);

      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
        setIsAnimating(false);
      }, 1000);

      return () => clearTimeout(timer);
    } else if (countdown === 0) {
      // Stop countdown tick before playing game start sound
      if (audioManager.isReady()) {
        audioManager.stop(GAME_SOUNDS.COUNTDOWN_TICK.id);
        audioManager.play(GAME_SOUNDS.GAME_START.id);
      }

      // Show "GO!" for a brief moment before completing
      const timer = setTimeout(() => {
        onCountdownCompleteRef.current?.();
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [countdown, isSyncedCountdown]);

  useEffect(() => {
    if (!isSyncedCountdown) {
      previousDisplayCountRef.current = null;
      return;
    }

    if (displayCount === previousDisplayCountRef.current) {
      return;
    }

    previousDisplayCountRef.current = displayCount;

    if (displayCount > 0) {
      if (audioManager.isReady()) {
        audioManager.stop(GAME_SOUNDS.COUNTDOWN_TICK.id);
        audioManager.play(GAME_SOUNDS.COUNTDOWN_TICK.id);
      }

      setIsAnimating(true);
      const animationTimer = setTimeout(() => {
        setIsAnimating(false);
      }, 300);

      return () => clearTimeout(animationTimer);
    }

    if (audioManager.isReady()) {
      audioManager.stop(GAME_SOUNDS.COUNTDOWN_TICK.id);
      audioManager.play(GAME_SOUNDS.GAME_START.id);
    }

    setIsAnimating(true);
    const animationTimer = setTimeout(() => {
      setIsAnimating(false);
    }, 300);

    return () => clearTimeout(animationTimer);
  }, [displayCount, isSyncedCountdown]);

  const getCountdownText = (count: number) => {
    if (count > 0) return count.toString();
    return 'GO!';
  };

  const getCountdownColor = (count: number) => {
    if (count === 0) return '#10B981'; // Green for GO!
    if (count === 1) return '#F59E0B'; // Orange for 1
    return '#EF4444'; // Red for 3,2
  };

  return (
    <div style={{
      background: 'linear-gradient(135deg, #2563EB 0%, #1E40AF 25%, #059669 75%, #047857 100%)',
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Animated background particles */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: `
          radial-gradient(circle at 20% 20%, rgba(255,255,255,0.1) 0%, transparent 50%),
          radial-gradient(circle at 80% 80%, rgba(255,255,255,0.1) 0%, transparent 50%),
          radial-gradient(circle at 40% 90%, rgba(255,255,255,0.05) 0%, transparent 50%)
        `,
        animation: 'float 3s ease-in-out infinite',
        pointerEvents: 'none'
      }} />

      <div style={{
        textAlign: 'center',
        position: 'relative',
        zIndex: 1
      }}>
        {/* Main countdown display */}
        <div style={{
          fontSize: displayCount === 0 ? '8rem' : '12rem',
          fontWeight: 'bold',
          color: getCountdownColor(displayCount),
          textShadow: '0 8px 32px rgba(0,0,0,0.4)',
          transform: isAnimating ? 'scale(1.2)' : 'scale(1)',
          transition: 'all 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
          marginBottom: '2rem',
          animation: displayCount === 0 ? 'pulse 0.6s ease-in-out infinite' : 'none'
        }}>
          {getCountdownText(displayCount)}
        </div>

        {/* Subtitle */}
        <div style={{
          fontSize: '2.5rem',
          fontWeight: '600',
          marginBottom: '3rem',
          opacity: 0.9,
          animation: 'fadeInUp 0.8s ease-out'
        }}>
          {displayCount > 0 ? 'Get Ready!' : 'Let\'s Play!'}
        </div>

        {/* Game info */}
        <div style={{
          backgroundColor: 'rgba(255,255,255,0.1)',
          padding: '2rem 3rem',
          borderRadius: '20px',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255,255,255,0.2)',
          animation: 'fadeInUp 1s ease-out 0.3s backwards'
        }}>
          <div style={{
            fontSize: '1.5rem',
            fontWeight: '500',
            marginBottom: '0.5rem'
          }}>
            🎮 Quiz Game Starting
          </div>
          <div style={{
            fontSize: '1.2rem',
            opacity: 0.8
          }}>
            Answer questions by running to the correct zones!
          </div>
        </div>

        {/* Teacher controls */}
        {isTeacher && (
          <div style={{
            position: 'fixed',
            bottom: '2rem',
            right: '2rem',
            zIndex: 1000
          }}>
            <button
              onClick={onCountdownComplete}
              style={{
                backgroundColor: 'rgba(255,255,255,0.2)',
                color: 'white',
                border: '2px solid rgba(255,255,255,0.3)',
                borderRadius: '50px',
                padding: '1rem 2rem',
                fontSize: '1rem',
                fontWeight: 'bold',
                cursor: 'pointer',
                backdropFilter: 'blur(10px)',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '0.8rem'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.3)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <span style={{ fontSize: '1.2rem' }}>⏭️</span>
              Skip Countdown
            </button>
          </div>
        )}
      </div>

      {/* CSS animations */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.8; transform: scale(1.1); }
        }

        @keyframes fadeInUp {
          0% {
            opacity: 0;
            transform: translateY(30px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          33% { transform: translateY(-10px) rotate(1deg); }
          66% { transform: translateY(-5px) rotate(-1deg); }
        }
      `}</style>
    </div>
  );
};

export default CountdownScreen;
